import Axios, { AxiosResponse } from 'axios';
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, snackbarSettingsAtom } from "../../hooks";
import { useLogin } from "../login";
import {
    adminActiveReportsAtom,
    adminActiveReportsLoadingAtom,
    adminActiveReportsTotalAtom,
    adminErrorReportDetailAtom,
    adminHandledReportsAtom,
    adminHandledReportsLoadingAtom,
    adminHandledReportsTotalAtom,
    CloseErrorReportPayload,
    ErrorReport,
    ErrorReportBucket,
    ErrorReportDetail,
    ErrorReportFilters,
    ErrorReportListItem,
    ErrorReportListResponse,
    ErrorReportNote,
    ErrorReportStatus,
    ExoFlowOptions,
    ExoFlowPushPreview,
    myErrorReportsAtom,
    PushToExoFlowPayload,
    SubmitErrorReportPayload,
} from "./types";

// Active carries the working queue so it shows more per page; Handled is an
// archive that is usually collapsed, so it starts small.
export const ERROR_REPORT_DEFAULT_PAGE_SIZES: { [bucket in ErrorReportBucket]: number } = {
    active: 25,
    handled: 10,
    deleted: 10,
};

export const MAX_ERROR_REPORT_IMAGES = 3;
export const MAX_ERROR_REPORT_IMAGE_BYTES = 5 * 1024 * 1024;
export const ALLOWED_ERROR_REPORT_MIMES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

// #region errorReportFileToBase64
// FileReader → data URL. The backend derives the mime from this prefix, so the
// prefix must be preserved (it is NOT stripped client-side).
export const errorReportFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
};

// #region validateImageFile
// Client-side mirror of the server checks so the user gets instant feedback
// instead of an opaque 400/413 after a long upload.
export const validateImageFile = (file: File): string | null => {
    if (!ALLOWED_ERROR_REPORT_MIMES.includes(file.type)) {
        return `${file.name}: only PNG, JPEG, WebP or GIF images can be attached.`;
    }
    if (file.size > MAX_ERROR_REPORT_IMAGE_BYTES) {
        return `${file.name}: images must be 5MB or smaller.`;
    }
    return null;
};

export type SubmitErrorReportResult =
    | { ok: true; report: ErrorReport }
    | { ok: false; message: string; rateLimited: boolean };

type ErrorReportFunctions = {
    submitErrorReport: (payload: SubmitErrorReportPayload) => Promise<SubmitErrorReportResult>;
    getMyErrorReports: () => Promise<void>;
    listErrorReports: (bucket: ErrorReportBucket, page: number, limit: number, filters: ErrorReportFilters) => Promise<void>;
    getErrorReport: (id: number) => Promise<ErrorReportDetail | null>;
    getErrorReportImageUrl: (id: number, imageId: number) => Promise<string | null>;
    updateErrorReportStatus: (id: number, status: ErrorReportStatus, notifyReporter: boolean, resolutionNote?: string) => Promise<boolean>;
    addErrorReportNote: (id: number, note: string) => Promise<ErrorReportNote | null>;
    closeErrorReport: (id: number, payload: CloseErrorReportPayload) => Promise<ErrorReport | null>;
    deleteErrorReport: (id: number) => Promise<boolean>;
    restoreErrorReport: (id: number) => Promise<boolean>;
    pushErrorReportToExoFlow: (id: number, payload: PushToExoFlowPayload) => Promise<ErrorReport | null>;
    getExoFlowPreview: (id: number) => Promise<ExoFlowPushPreview | null>;
    getExoFlowOptions: () => Promise<ExoFlowOptions | null>;
};

// #region useErrorReports
export const useErrorReports = (): [ErrorReport[], ErrorReportDetail | null, ErrorReportFunctions] => {
    const [myReports, setMyReports] = useAtom(myErrorReportsAtom);
    const [reportDetail, setReportDetail] = useAtom(adminErrorReportDetailAtom);
    const setActiveReports = useSetAtom(adminActiveReportsAtom);
    const setActiveTotal = useSetAtom(adminActiveReportsTotalAtom);
    const setActiveLoading = useSetAtom(adminActiveReportsLoadingAtom);
    const setHandledReports = useSetAtom(adminHandledReportsAtom);
    const setHandledTotal = useSetAtom(adminHandledReportsTotalAtom);
    const setHandledLoading = useSetAtom(adminHandledReportsLoadingAtom);

    // A mutated report can be sitting in either table (it usually moves between
    // them), so writes fan out to both rather than guessing which one holds it.
    const patchBothLists = (id: number, patch: Partial<ErrorReportListItem>) => {
        const apply = (previous: ErrorReportListItem[]) =>
            previous.map((report) => report.id === id ? { ...report, ...patch } : report);
        setActiveReports(apply);
        setHandledReports(apply);
    };

    const removeFromBothLists = (id: number) => {
        const drop = (previous: ErrorReportListItem[]) => previous.filter((report) => report.id !== id);
        setActiveReports(drop);
        setHandledReports(drop);
    };

    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [, authToken, loginFunctions] = useLogin();

    const headers = { 'X-Wallmates-Auth': authToken };

    const showError = (text: string) => {
        setSnackbar({ show: true, snackbarLevel: 'error', text });
    };

    // #region submitErrorReport
    // 429 comes back with a friendly server message; surface it verbatim rather
    // than the generic failure text.
    const submitErrorReport = async (payload: SubmitErrorReportPayload): Promise<SubmitErrorReportResult> => {
        try {
            const response: AxiosResponse<ErrorReport> = await Axios.post(
                environment.errorReportUrl,
                payload,
                { headers }
            );
            setMyReports((previous: ErrorReport[]) => [response.data, ...previous]);
            return { ok: true, report: response.data };
        } catch (err: any) {
            const status = err?.response?.status;
            if (status === 429) {
                return { ok: false, rateLimited: true, message: err?.response?.data?.message || 'Too many reports — please wait before sending another.' };
            }
            if (status === 400) {
                return { ok: false, rateLimited: false, message: err?.response?.data?.message || 'Please check the form and try again.' };
            }
            if (status === 413) {
                return { ok: false, rateLimited: false, message: 'Those screenshots are too large. Try removing one or attaching a smaller image.' };
            }
            errorCatcher(err, loginFunctions.logout);
            return { ok: false, rateLimited: false, message: 'There was an error while sending your report. Please try again later.' };
        }
    };

    // #region getMyErrorReports
    const getMyErrorReports = async (): Promise<void> => {
        const response: AxiosResponse<ErrorReport[]> | void = await Axios.get(
            `${environment.errorReportUrl}/mine`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (response) setMyReports(response.data);
    };

    // #region listErrorReports
    // One call per section. `deleted` shares the Handled section's atoms because
    // the two swap into the same table.
    const listErrorReports = async (
        bucket: ErrorReportBucket,
        page: number,
        limit: number,
        filters: ErrorReportFilters
    ): Promise<void> => {
        const isActive = bucket === 'active';
        const setReports = isActive ? setActiveReports : setHandledReports;
        const setTotal = isActive ? setActiveTotal : setHandledTotal;
        const setLoading = isActive ? setActiveLoading : setHandledLoading;

        setLoading(true);

        const params = new URLSearchParams({ bucket, page: String(page), limit: String(limit) });
        Object.entries(filters).forEach(([key, value]) => {
            if (key === 'bucket') return;
            if (typeof value === 'string' && value.trim().length > 0) params.set(key, value.trim());
        });

        const response: AxiosResponse<ErrorReportListResponse> | void = await Axios.get(
            `${environment.errorReportUrl}?${params.toString()}`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (response) {
            setReports(response.data.reports);
            setTotal(response.data.total);
        } else {
            showError('There was an error while loading error reports. Please try again later.');
        }
        setLoading(false);
    };

    // #region getErrorReport
    const getErrorReport = async (id: number): Promise<ErrorReportDetail | null> => {
        const response: AxiosResponse<ErrorReportDetail> | void = await Axios.get(
            `${environment.errorReportUrl}/${id}`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (!response) {
            showError('There was an error while loading that report. Please try again later.');
            setReportDetail(null);
            return null;
        }
        setReportDetail(response.data);
        return response.data;
    };

    // #region getErrorReportImageUrl
    // Screenshot links are signed and time-limited, so they are minted on click
    // rather than reused from the (possibly stale) detail payload.
    const getErrorReportImageUrl = async (id: number, imageId: number): Promise<string | null> => {
        const response: AxiosResponse<{ url: string }> | void = await Axios.get(
            `${environment.errorReportUrl}/${id}/images/${imageId}/url`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (!response?.data?.url) {
            showError('That screenshot link could not be opened. Please try again.');
            return null;
        }
        return response.data.url;
    };

    // #region updateErrorReportStatus
    const updateErrorReportStatus = async (
        id: number,
        status: ErrorReportStatus,
        notifyReporter: boolean,
        resolutionNote?: string
    ): Promise<boolean> => {
        try {
            const response: AxiosResponse<ErrorReport> = await Axios.patch(
                `${environment.errorReportUrl}/${id}/status`,
                { status, notify_reporter: notifyReporter, resolution_note: resolutionNote },
                { headers }
            );
            patchBothLists(id, response.data);
            setReportDetail((previous) => previous && previous.id === id ? { ...previous, ...response.data } : previous);
            setSnackbar({ show: true, snackbarLevel: 'success', text: 'Status updated.' });
            return true;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            showError(err?.response?.data?.message || 'There was an error while updating the status.');
            return false;
        }
    };

    // #region addErrorReportNote
    const addErrorReportNote = async (id: number, note: string): Promise<ErrorReportNote | null> => {
        try {
            const response: AxiosResponse<ErrorReportNote> = await Axios.post(
                `${environment.errorReportUrl}/${id}/notes`,
                { note },
                { headers }
            );
            setReportDetail((previous) =>
                previous && previous.id === id ? { ...previous, notes: [...previous.notes, response.data] } : previous
            );
            setActiveReports((previous) => previous.map((report) =>
                report.id === id ? { ...report, note_count: report.note_count + 1 } : report
            ));
            setHandledReports((previous) => previous.map((report) =>
                report.id === id ? { ...report, note_count: report.note_count + 1 } : report
            ));
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            showError(err?.response?.data?.message || 'There was an error while adding the note.');
            return null;
        }
    };

    // #region closeErrorReport
    // Closing moves the report out of Active and into Handled, so the caller is
    // expected to reload both sections afterwards — the local patch only keeps
    // whichever table is currently on screen from showing a stale status badge.
    const closeErrorReport = async (id: number, payload: CloseErrorReportPayload): Promise<ErrorReport | null> => {
        try {
            const response: AxiosResponse<ErrorReport> = await Axios.post(
                `${environment.errorReportUrl}/${id}/close`,
                payload,
                { headers }
            );
            patchBothLists(id, response.data);
            setReportDetail((previous) => previous && previous.id === id ? { ...previous, ...response.data } : previous);
            setSnackbar({ show: true, snackbarLevel: 'success', text: 'Report closed.' });
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            showError(err?.response?.data?.message || 'There was an error while closing the report.');
            return null;
        }
    };

    // #region deleteErrorReport
    // Soft delete — the row disappears from both sections but is still reachable
    // from the Handled section's "show deleted" view.
    const deleteErrorReport = async (id: number): Promise<boolean> => {
        try {
            const response: AxiosResponse<ErrorReport> = await Axios.delete(
                `${environment.errorReportUrl}/${id}`,
                { headers }
            );
            removeFromBothLists(id);
            setReportDetail((previous) => previous && previous.id === id ? { ...previous, ...response.data } : previous);
            setSnackbar({ show: true, snackbarLevel: 'success', text: 'Report deleted.' });
            return true;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            showError(err?.response?.data?.message || 'There was an error while deleting the report.');
            return false;
        }
    };

    // #region restoreErrorReport
    const restoreErrorReport = async (id: number): Promise<boolean> => {
        try {
            const response: AxiosResponse<ErrorReport> = await Axios.post(
                `${environment.errorReportUrl}/${id}/restore`,
                {},
                { headers }
            );
            removeFromBothLists(id);
            setReportDetail((previous) => previous && previous.id === id ? { ...previous, ...response.data } : previous);
            setSnackbar({ show: true, snackbarLevel: 'success', text: 'Report restored.' });
            return true;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            showError(err?.response?.data?.message || 'There was an error while restoring the report.');
            return false;
        }
    };

    // #region pushErrorReportToExoFlow
    // 409 = already pushed (another tab won the race); 503 = integration not
    // configured. Both surface the server message so the admin knows which.
    const pushErrorReportToExoFlow = async (id: number, payload: PushToExoFlowPayload): Promise<ErrorReport | null> => {
        try {
            const response: AxiosResponse<ErrorReport> = await Axios.post(
                `${environment.errorReportUrl}/${id}/exo-flow`,
                payload,
                { headers }
            );
            patchBothLists(id, response.data);
            setReportDetail((previous) => previous && previous.id === id ? { ...previous, ...response.data } : previous);
            setSnackbar({ show: true, snackbarLevel: 'success', text: 'Pushed to EXO Flow.' });
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            showError(err?.response?.data?.message || 'There was an error while pushing to EXO Flow.');
            return null;
        }
    };

    // #region getExoFlowPreview
    // Generated title/body/screenshot footer for the pre-push dialog.
    const getExoFlowPreview = async (id: number): Promise<ExoFlowPushPreview | null> => {
        const response: AxiosResponse<ExoFlowPushPreview> | void = await Axios.get(
            `${environment.errorReportUrl}/${id}/exo-flow/preview`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        return response ? response.data : null;
    };

    // #region getExoFlowOptions
    const getExoFlowOptions = async (): Promise<ExoFlowOptions | null> => {
        const response: AxiosResponse<ExoFlowOptions> | void = await Axios.get(
            `${environment.errorReportUrl}/exo-flow/options`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        return response ? response.data : null;
    };

    return [
        myReports,
        reportDetail,
        {
            submitErrorReport,
            getMyErrorReports,
            listErrorReports,
            getErrorReport,
            getErrorReportImageUrl,
            updateErrorReportStatus,
            addErrorReportNote,
            closeErrorReport,
            deleteErrorReport,
            restoreErrorReport,
            pushErrorReportToExoFlow,
            getExoFlowPreview,
            getExoFlowOptions,
        },
    ];
};
